import { AlertTriangle, CheckCircle2, Thermometer } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { TransferCandidate } from '@/types/storage'
import { summariseMoves, type ReviewableMove } from './movePreview'

/**
 * What is wrong with this pairing, before anyone commits to it.
 *
 * Deliberately shown as a step of its own rather than a line under the picker:
 * a shortfall and a temperature mismatch are the two ways a replacement turns
 * out to be the wrong machine, and both are cheap to fix now and expensive to
 * fix with a technician standing at an open freezer.
 */
export function CompatibilityReport({
  candidate,
  moves,
  sourceCode,
}: {
  candidate: TransferCandidate
  moves: ReviewableMove[]
  sourceCode: string
}) {
  const summary = summariseMoves(moves)

  const findings = [
    {
      key: 'capacity',
      ok: candidate.fits,
      icon: AlertTriangle,
      okText: `${candidate.code} has room for all ${summary.total} occupant${summary.total === 1 ? '' : 's'} (${candidate.remaining} free).`,
      badText: `${candidate.code} is ${candidate.shortfall} space${candidate.shortfall === 1 ? '' : 's'} short — ${candidate.remaining} free for ${summary.total} occupants.`,
    },
    {
      key: 'condition',
      ok: candidate.conditionMatches,
      icon: Thermometer,
      okText: `Both units run at the same storage condition.`,
      badText: `${candidate.code} runs at a different storage condition from ${sourceCode}. Every move into it will need reviewing.`,
    },
    {
      key: 'mapping',
      ok: summary.unmapped === 0,
      icon: AlertTriangle,
      okText: 'Every occupant maps onto the same address in the replacement.',
      badText: `${summary.unmapped} occupant${summary.unmapped === 1 ? ' has' : 's have'} no matching address in ${candidate.code} — its layout differs. Retarget each by hand on the next step.`,
    },
    {
      key: 'verdict',
      ok: summary.conditionFailures === 0,
      icon: Thermometer,
      okText: 'No move would land somewhere unsuitable.',
      badText: `${summary.conditionFailures} move${summary.conditionFailures === 1 ? '' : 's'} would land somewhere unsuitable for what ${summary.conditionFailures === 1 ? 'it holds' : 'they hold'}.`,
    },
  ]

  const problems = findings.filter((f) => !f.ok)

  return (
    <div className='space-y-3'>
      <div
        className={cn(
          'rounded-md border p-3 text-sm',
          problems.length === 0
            ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
            : 'border-amber-300 bg-amber-50 text-amber-900',
        )}
      >
        <p className='font-medium'>
          {problems.length === 0
            ? `${candidate.code} is a like-for-like replacement.`
            : `${problems.length} thing${problems.length === 1 ? '' : 's'} to settle before this plan can be approved.`}
        </p>
        <p className='mt-0.5 text-[13px] opacity-90'>
          {/* Never "no candidates": a poor match is ranked down, not hidden,
              because an unplanned swap may have no good option left. */}
          {problems.length === 0
            ? 'You can still retarget individual moves on the next step.'
            : 'You can continue anyway — nothing is committed until you approve the plan.'}
        </p>
      </div>

      <ul className='space-y-2'>
        {findings.map((f) => {
          const Icon = f.ok ? CheckCircle2 : f.icon
          return (
            <li key={f.key} className='flex items-start gap-2 text-sm'>
              <Icon
                className={cn(
                  'mt-0.5 h-4 w-4 shrink-0',
                  f.ok ? 'text-emerald-600' : 'text-amber-600',
                )}
              />
              <span className={f.ok ? 'text-muted-foreground' : 'text-foreground'}>
                {f.ok ? f.okText : f.badText}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
